/**
 * One way to open a row, on every list in the back office. R-270.
 *
 * ── THE PROBLEM ─────────────────────────────────────────────────────────────
 *
 * There were three. Orders and quotes put a link in the first cell. Stock put
 * one on the name but not the code beside it. Customers put an "Open" button at
 * the far right of the row, so the target moved depending on which screen you
 * were on and how wide the window was. Christian noticed before any of the
 * staff did: "I noticed we have different sets of UIs which is confusing."
 *
 * A person learns one gesture and expects it everywhere. Three gestures is
 * three things to learn and two chances to feel stupid.
 *
 * ── THE RULE ────────────────────────────────────────────────────────────────
 *
 * The whole row opens the thing. Click anywhere on it that is not itself a
 * control.
 *
 * ── WHY THE REAL LINK STAYS ─────────────────────────────────────────────────
 *
 * A `<tr onClick>` alone would be a regression dressed as an improvement. It
 * cannot be focused, a screen reader announces nothing, and middle click or
 * cmd click cannot open it in a new tab, which is how anybody works through a
 * list of orders.
 *
 * So the anchor in the first cell stays and keeps doing all of that. This adds
 * a mouse affordance over the rest of the row and nothing else. The row is not
 * given `tabIndex`, because that would put every row in the tab order twice.
 */
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react';

/** Anything that handles its own click. A row must never steal one. */
const INTERACTIVE = 'a, button, input, select, textarea, label, summary, [role="button"]';

export interface OpenRowProps {
  /** What opening means. Navigate, or expand in place. */
  onOpen: () => void;
  /** Read to somebody using a screen reader, on the row's own control. */
  label?: string;
  children: ReactNode;
  className?: string;
}

/**
 * Should this click open the row?
 *
 * Three things say no, and each one is a real way people use a table:
 *
 *   The click landed on a control. The button in that cell has its own job.
 *
 *   A modifier is held. Cmd, ctrl, shift and middle click belong to the anchor,
 *   which opens a new tab. Handling them here would break that quietly.
 *
 *   Text is selected. Somebody dragging across a reference to copy it is not
 *   asking to navigate, and a row that jumps away mid-selection loses the thing
 *   they were copying.
 */
export function shouldOpen(event: MouseEvent<HTMLElement>): boolean {
  if (event.defaultPrevented) return false;
  if (event.button !== 0) return false;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
  if ((event.target as HTMLElement).closest(INTERACTIVE)) return false;

  const selection = typeof window !== 'undefined' ? window.getSelection() : null;
  if (selection && selection.toString().length > 0) return false;

  return true;
}

/** The props a row needs. Spread onto a `<tr>`, or onto a grid row that is a div. */
export function openRowProps({ onOpen, label }: { onOpen: () => void; label?: string }) {
  return {
    onClick: (event: MouseEvent<HTMLElement>) => {
      if (shouldOpen(event)) onOpen();
    },
    /**
     * Enter only, and only when the row itself has focus.
     *
     * Space is left alone: in a table it scrolls the page, and taking that away
     * to save one keystroke is a poor trade.
     */
    onKeyDown: (event: KeyboardEvent<HTMLElement>) => {
      if (event.key !== 'Enter') return;
      if (event.target !== event.currentTarget) return;
      onOpen();
    },
    'aria-label': label,
    className: 'cursor-pointer',
  };
}

/** A table row that opens its record. */
export function OpenRow({ onOpen, label, children, className }: OpenRowProps) {
  const props = openRowProps({ onOpen, label });
  return (
    <tr
      {...props}
      className={`${props.className} transition-colors hover:bg-raised ${className ?? ''}`}
    >
      {children}
    </tr>
  );
}
